import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from '@angular/core';

import { FormErrorsModule } from '@lib/frontend-shared-ui-ionic-form-errors';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule, IonInput } from '@ionic/angular';
import {
    PasswordInputToggleDirective,
    PasswordStrengthTooltipDirective,
} from '@lib/frontend-shared-ui-ionic-form-utilities';
import { RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { fromFormControl } from '@lib/shared-util-rxjs';

@Component({
    standalone: true,
    selector: 'lib-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormErrorsModule,
        IonicModule,
        ReactiveFormsModule,
        RouterModule,
        PasswordInputToggleDirective,
        PasswordStrengthTooltipDirective,
    ],
})
export class LoginFormComponent {
    @Output() public submitLogin = new EventEmitter<LoginFormOutput>();

    protected readonly loginForm = this.formBuilder.group({
        username: this.formBuilder.control('', [Validators.required, Validators.email]),
        password: this.formBuilder.control('', Validators.required),
    });
    public readonly username$: Observable<string> = fromFormControl(
        this.loginForm.controls.username,
    );
    protected readonly validationMessages = VALIDATION_MESSAGES;

    @ViewChild('usernameInput', { static: false })
    private usernameInput!: IonInput;

    @ViewChild('passwordInput', { static: false })
    private passwordInput!: IonInput;

    public constructor(private readonly formBuilder: NonNullableFormBuilder) {}

    public submitForm(): void {
        const value = this.loginForm.getRawValue();
        this.submitLogin.emit(value);
    }

    public fillAndFocus(email?: string) {
        if (email) this.loginForm.patchValue({ username: email });
        const input = email ? this.passwordInput : this.usernameInput;

        // Without this, for some reason the timing for focus was off.
        setTimeout(() => void input.setFocus(), 500);
    }
}

const VALIDATION_MESSAGES = {
    username: {
        required: 'Email is required.',
        email: 'Invalid email address.',
    },
    password: {
        required: 'Password is required.',
    },
} as const;

export interface LoginFormOutput {
    username: string;
    password: string;
}
